/**
 * Application Capalys.
 *
 * Rien n'est accessible sans compte. Ce n'est pas une précaution de principe : les
 * données de production d'une usine sont ce qu'elle a de plus sensible après ses
 * finances, et un outil qui les afficherait derrière une simple URL ne passerait aucun
 * audit client.
 *
 * Deux écrans, dans un ordre qui n'est pas neutre.
 *
 * **Analyser un fichier** vient en premier : c'est la réponse à « comment vos données
 * entrent-elles ? », et c'est là que se joue la différence avec un tableur. Aucun outil
 * du marché n'avale un export machine inconnu.
 *
 * **Projets** vient ensuite : « où sont mes données ? ». C'est l'écran qui répond à la
 * phrase qu'un utilisateur nous a dite et qui coûtait le plus cher — « je me suis
 * reconnecté, je n'ai rien trouvé ».
 *
 * **Vue Caractéristique** vient en dernier : « qu'est-ce que j'obtiens ? ». Cartes,
 * indices, stratification. C'est important, mais tout le monde en fait.
 */

import { FolderOpen, Table2, Upload, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { AppShell, Logo, type Tab } from "./AppShell";
import { AuthScreen } from "./AuthScreen";
import { CharacteristicView } from "./CharacteristicView";
import { InvitationScreen } from "./InvitationScreen";
import { MappingWizard } from "./MappingWizard";
import { MembersView } from "./MembersView";
import { ProjectsView } from "./ProjectsView";
import { invitationFromUrl } from "./membersApi";
import { SessionProvider, useSession } from "./ui/session";
import { ThemeProvider } from "./ui/theme";

const ANALYSE: Tab = {
  id: "mapping",
  label: "Analyser un fichier",
  icon: <Upload className="h-4 w-4" />,
  hint: "Export machine brut",
};

const PROJETS: Tab = {
  id: "projects",
  label: "Projets",
  icon: <FolderOpen className="h-4 w-4" />,
  hint: "Séries accumulées",
};

const CARACTERISTIQUE: Tab = {
  id: "characteristic",
  label: "Vue Caractéristique",
  icon: <Table2 className="h-4 w-4" />,
  hint: "Cartes et capabilité",
};

const MEMBRES: Tab = {
  id: "members",
  label: "Membres",
  icon: <Users className="h-4 w-4" />,
  hint: "Accès à l'organisation",
};

/**
 * Attente de la première réponse du serveur.
 *
 * Sans cet état, l'écran de connexion apparaîtrait un instant à chaque rechargement,
 * même pour quelqu'un de parfaitement connecté — un clignotement qui suffit à faire
 * douter de la solidité du produit.
 */
function Patience() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <Logo className="h-8 w-8 animate-pulse" />
    </div>
  );
}

function Interieur() {
  const { identity } = useSession();

  // Le technicien consulte, il ne dépose pas de fichier : le serveur le lui refuserait
  // en 403. Masquer l'onglet plutôt que laisser cliquer vers un refus — une interface
  // qui propose une action interdite fait perdre du temps et donne l'air d'être cassée.
  // « Projets » reste visible pour le technicien : consulter n'est pas modifier, et
  // c'est précisément l'écran dont il a besoin — celui qui montre les séries déjà
  // accumulées. Seul le dépôt lui est fermé.
  // « Membres » est visible de tous : savoir à qui demander une correction fait partie du
  // travail d'un technicien, et ces personnes se croisent tous les jours dans le même
  // atelier. Seules les actions — inviter, changer un rôle, retirer — sont réservées à
  // l'administrateur, et c'est l'écran qui les masque.
  const tabs = useMemo<Tab[]>(
    () =>
      identity?.role === "technician"
        ? [PROJETS, CARACTERISTIQUE, MEMBRES]
        : [ANALYSE, PROJETS, CARACTERISTIQUE, MEMBRES],
    [identity?.role],
  );

  const [tab, setTab] = useState(() =>
    identity?.role === "technician" ? PROJETS.id : ANALYSE.id,
  );

  return (
    <AppShell tabs={tabs} current={tab} onNavigate={setTab}>
      {tab === ANALYSE.id ? (
        <MappingWizard />
      ) : tab === PROJETS.id ? (
        <ProjectsView />
      ) : tab === MEMBRES.id ? (
        <MembersView />
      ) : (
        <CharacteristicView />
      )}
    </AppShell>
  );
}

function Portail() {
  const { status } = useSession();

  // L'invitation passe **avant** la question de la session, et c'est le seul écran dans
  // ce cas : celui qui arrive par ce lien n'a par définition pas de compte, et le
  // renvoyer vers la connexion lui demanderait des identifiants qu'il vient précisément
  // recevoir. Lu une fois au premier rendu — le jeton ne change pas sous l'application.
  const [invitation] = useState(invitationFromUrl);
  if (invitation) return <InvitationScreen token={invitation} />;

  if (status === "unknown") return <Patience />;
  if (status === "anonymous") return <AuthScreen />;
  return <Interieur />;
}

export function App() {
  return (
    <ThemeProvider>
      <SessionProvider>
        <Portail />
      </SessionProvider>
    </ThemeProvider>
  );
}
